/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { EntityLabel } from "./entity-label";

type Entity = {
  type: string;
  properties: Entity[];
  confidence?: number;
  id?: string;
};

// Classification label used when classifying a document
export class ClassificationLabel {
  label: string = "Document type";
  values: EntityLabel[];
  id?: string;
  selectedValue?: string;

  constructor(values: EntityLabel[] = []) {
    this.values = values;
  }

  static fromJson(json: any): ClassificationLabel {
    const classificationLabel = new ClassificationLabel(
      json.values
    );
    return classificationLabel;
  }
}

// get entity labels for entity dropdown list from list of document entities
export function convertEntitiesToClassificationLabels(
  entities: Entity[]
): ClassificationLabel[] {
  console.log("DEBUG convertEntitiesToClassificationLabels - input entities:", entities);

  const entityLabels: EntityLabel[] = [];
  let maxEntityConfidence = 0;
  let selectedValue: string | undefined;
  let selectedId: string | undefined;

  entities?.forEach((entity: Entity) => {
    console.log("DEBUG - Processing entity:", { type: entity.type, confidence: entity.confidence, id: entity.id });

    // Create EntityLabel for each unique entity type
    if (!entityLabels.some((label) => label.label === entity.type)) {
      const newEntityLabel = new EntityLabel(entity.type);

      // Store original name (without ID prefix) for backend processing
      // This ensures consistency with labels created from processed_data
      // Updated regex to handle both dash and underscore formats:
      // - "1 - Neuschaden" (dash with spaces)
      // - "5_kuendigung" (underscore without spaces)
      // - "5 _ kuendigung" (underscore with spaces)
      const idPrefixMatch = entity.type.match(/^(\d+)\s*[-_]\s*(.+)$/);
      if (idPrefixMatch) {
        (newEntityLabel as any).originalDisplayName = idPrefixMatch[2];
        console.log("DEBUG - Stored originalDisplayName:", idPrefixMatch[2]);
      }

      entityLabels.push(newEntityLabel);
      console.log("DEBUG - Created EntityLabel:", newEntityLabel);

      // Track the highest confidence entity for selection
      if (entity.confidence ?? 0 >= maxEntityConfidence) {
        maxEntityConfidence = entity.confidence ?? 0;
        // Strip ID prefix from selectedValue to avoid backend KeyError
        // Format: "1 - Neuschaden" -> "Neuschaden" OR "5_kuendigung" -> "kuendigung"
        selectedValue = idPrefixMatch ? idPrefixMatch[2] : entity.type;
        selectedId = entity.id;
        console.log("DEBUG - Set selectedValue:", { original: entity.type, stripped: selectedValue });
      }
    }
  });

  console.log("DEBUG - Final entityLabels array:", entityLabels);

  const classificationLabel = new ClassificationLabel(entityLabels);
  classificationLabel.selectedValue = selectedValue;
  classificationLabel.id = selectedId;

  console.log("DEBUG - Final ClassificationLabel:", classificationLabel);

  return [classificationLabel];
}
